import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useClients } from '@/modules/commercial/hooks';
import { getCommercialOperation } from '@/modules/commercial/services';
import {
  InvoiceStatusBadge,
  InvoiceTypeBadge,
} from '@/modules/billing/components';
import { cancelInvoice, getInvoice } from '@/modules/billing/services';
import { INVOICE_STATUS_LABELS } from '@/modules/billing/types';
import { modulePaths } from '@/routes/paths';
import { EntityAuditTrail } from '@/shared/components/EntityAuditTrail';
import { useAuth } from '@/shared/hooks/useAuth';

export function InvoiceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { hasPermission } = useAuth();
  const canWrite = hasPermission('billing.write');
  const { clients } = useClients();

  const [invoice, setInvoice] = useState<Awaited<ReturnType<typeof getInvoice>>>(
    null,
  );
  const [operationTitle, setOperationTitle] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    void getInvoice(id)
      .then((inv) => {
        if (!inv) {
          setError('Factura no encontrada.');
          return;
        }
        setInvoice(inv);
        if (inv.commercialOperationId) {
          void getCommercialOperation(inv.commercialOperationId).then((op) =>
            setOperationTitle(op?.title ?? null),
          );
        }
      })
      .catch(() => setError('Error al cargar la factura.'))
      .finally(() => setLoading(false));
  }, [id]);

  async function handleCancel() {
    if (!id || !canWrite) return;
    const ok = window.confirm('¿Cancelar esta factura?');
    if (!ok) return;
    setCancelling(true);
    setError(null);
    try {
      const updated = await cancelInvoice(id);
      setInvoice(updated);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'No se pudo cancelar la factura.',
      );
    } finally {
      setCancelling(false);
    }
  }

  if (loading) return <p className="auth-muted">Cargando…</p>;
  if (!invoice) {
    return (
      <section>
        <p className="auth-error">{error ?? 'Factura no encontrada.'}</p>
        <Link to={`${modulePaths.billing}/facturas`}>← Volver</Link>
      </section>
    );
  }

  const clientName = invoice.clientId
    ? (clients.find((c) => c.id === invoice.clientId)?.name ?? '—')
    : '—';

  return (
    <section className="reservation-detail">
      <p>
        <Link to={`${modulePaths.billing}/facturas`}>← Facturas</Link>
      </p>

      <div className="inventory-page-header">
        <h1>{invoice.invoiceNumber || 'Factura sin número'}</h1>
        <InvoiceTypeBadge type={invoice.type} />
        <InvoiceStatusBadge status={invoice.status} />
      </div>

      {canWrite && invoice.status === 'draft' ? (
        <div className="inventory-actions">
          <Link
            to={`${modulePaths.billing}/facturas/${invoice.id}/editar`}
            className="auth-button inventory-page-header__btn"
          >
            Editar
          </Link>
        </div>
      ) : null}

      {canWrite &&
      invoice.status !== 'cancelled' &&
      invoice.status !== 'draft' ? (
        <div className="inventory-actions">
          <button
            type="button"
            className="auth-button auth-button--secondary"
            disabled={cancelling}
            onClick={() => void handleCancel()}
          >
            {cancelling ? 'Cancelando…' : 'Cancelar factura'}
          </button>
        </div>
      ) : null}

      <dl className="reservation-detail-grid">
        <dt>Estado</dt>
        <dd>{INVOICE_STATUS_LABELS[invoice.status]}</dd>
        <dt>Cliente</dt>
        <dd>{clientName}</dd>
        <dt>Operación comercial</dt>
        <dd>
          {invoice.commercialOperationId ? (
            <Link
              to={`${modulePaths.commercial}/operaciones/${invoice.commercialOperationId}`}
            >
              {operationTitle ?? invoice.commercialOperationId}
            </Link>
          ) : (
            '—'
          )}
        </dd>
        <dt>Emisión</dt>
        <dd>{invoice.issueDate}</dd>
        <dt>Vencimiento</dt>
        <dd>{invoice.dueDate || '—'}</dd>
        <dt>Subtotal</dt>
        <dd>$ {invoice.subtotal.toLocaleString('es-AR')}</dd>
        <dt>Impuestos</dt>
        <dd>$ {invoice.taxes.toLocaleString('es-AR')}</dd>
        <dt>Total</dt>
        <dd>$ {invoice.total.toLocaleString('es-AR')}</dd>
        <dt>Moneda</dt>
        <dd>{invoice.currency}</dd>
        <dt>Notas</dt>
        <dd>{invoice.notes || '—'}</dd>
        <dt>ARCA (futuro)</dt>
        <dd>
          {invoice.arcaStatus || invoice.arcaCAE
            ? `${invoice.arcaStatus || '—'} / CAE: ${invoice.arcaCAE || '—'}`
            : 'Sin datos fiscales'}
        </dd>
        <dt>Actualizada</dt>
        <dd>{invoice.updatedAt.toLocaleString('es-AR')}</dd>
      </dl>

      <EntityAuditTrail entityType="invoice" entityId={invoice?.id} />

      {error ? <p className="auth-error">{error}</p> : null}
    </section>
  );
}
